import Point from '@arcgis/core/geometry/Point';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as esriProjection from '@arcgis/core/geometry/projection';
import Extent from '@arcgis/core/geometry/Extent';
import Polygon from '@arcgis/core/geometry/Polygon';
import stores from 'cd-eft/models/Stores';
import { LinearUnit, convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';

const projectsLibraryStore = stores.AppStore.projectsLibraryStore;

export class SurfaceTinNode {
  readonly x: number;
  readonly y: number;
  readonly z: number;

  constructor(x: number, y: number, z: number) {
    this.x = x;
    this.y = y;
    this.z = z;
  }
}

export class SurfaceTinFace {
  readonly nodeIndex1: number;
  readonly nodeIndex2: number;
  readonly nodeIndex3: number;

  constructor(nodeIndex1: number, nodeIndex2: number, nodeIndex3: number) {
    this.nodeIndex1 = nodeIndex1;
    this.nodeIndex2 = nodeIndex2;
    this.nodeIndex3 = nodeIndex3;
  }
}

interface ISurfaceTinNode {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface ISurfaceTinFace {
  readonly nodeIndex1: number;
  readonly nodeIndex2: number;
  readonly nodeIndex3: number;
}

export class SurfaceTin {
  readonly wkid: number;
  readonly elevationUnit: LinearUnit;
  readonly nodes: SurfaceTinNode[];
  readonly faces: SurfaceTinFace[];

  private extent: Extent = undefined as any;
  private points: Point[] = [];
  private polygons: Polygon[] = [];

  constructor(wkid: number, elevationUnit: LinearUnit, nodes?: ISurfaceTinNode[], faces?: ISurfaceTinFace[]) {
    this.wkid = wkid;
    this.elevationUnit = elevationUnit;
    this.nodes = [];
    this.faces = [];
    if (nodes) {
      for (const node of nodes) {
        this.nodes.push(new SurfaceTinNode(node.x, node.y, node.z));
      }
    }
    if (faces) {
      for (const face of faces) {
        this.faces.push(new SurfaceTinFace(face.nodeIndex1, face.nodeIndex2, face.nodeIndex3));
      }
    }
  }

  public findNode(x: number, y: number, z: number): SurfaceTinNode | undefined {
    const result = this.nodes.find((node) => {
      return node.x === x && node.y === y && node.z === z;
    });
    return result;
  }

  async project(wkid: number, elevationUnit: LinearUnit): Promise<SurfaceTin> {
    await esriProjection.load();

    const result = new SurfaceTin(wkid, elevationUnit);

    const sourceSR = projectsLibraryStore.projectSpatialReference!;
    const resultSR = new SpatialReference({ wkid: wkid });

    for (const node of this.nodes) {
      const sourcePoint = new Point();
      sourcePoint.spatialReference = sourceSR;
      sourcePoint.hasZ = true;
      sourcePoint.x = node.x;
      sourcePoint.y = node.y;
      sourcePoint.z = node.z;

      const resultPoint = esriProjection.project(sourcePoint, resultSR) as Point;
      if (this.elevationUnit !== elevationUnit) {
        resultPoint.z = convertLinearUnit(sourcePoint.z, this.elevationUnit, elevationUnit);
      }

      const resultNode = new SurfaceTinNode(resultPoint.x, resultPoint.y, resultPoint.z);

      result.nodes.push(resultNode);
    }

    for (const face of this.faces) {
      //   const ix1 = this.nodes.indexOf(face.node1);
      //   const ix2 = this.nodes.indexOf(face.node2);
      //   const ix3 = this.nodes.indexOf(face.node3);

      //   const resultNode1 = result.nodes[ix1];
      //   const resultNode2 = result.nodes[ix2];
      //   const resultNode3 = result.nodes[ix3];

      const resultFace = new SurfaceTinFace(face.nodeIndex1, face.nodeIndex2, face.nodeIndex3);
      result.faces.push(resultFace);
    }

    return result;
  }

  public findZ(x: number, y: number, shortList?: SurfaceTinFace[]): number | undefined {
    if (this.polygons.length !== this.faces.length) {
      this.rebuild();
    }

    const face = this.findFace(x, y, shortList);
    if (face !== undefined) {
      const node1 = this.nodes[face.nodeIndex1];
      const node2 = this.nodes[face.nodeIndex2];
      const node3 = this.nodes[face.nodeIndex3];

      const z = this.interpolateZ(node1, node2, node3, x, y);
      return z;
    }

    return undefined;
  }

  private _lastFoundFace: SurfaceTinFace | undefined;
  public findFace(x: number, y: number, shortList?: SurfaceTinFace[]): SurfaceTinFace | undefined {
    if (this.extent === undefined) {
      this.extent = this.computeExtent();
    }
    if (this.polygons.length !== this.faces.length) {
      this.rebuild();
    }

    //first check to see if location is within cached extent
    if (x >= this.extent.xmin && x <= this.extent.xmax && y >= this.extent.ymin && y <= this.extent.ymax) {
      // TODO (sort of done, made shortlist function, not a proper index): to improve performance, build a spatial index grid to more quickly find potential hit faces
      const spatialReference = projectsLibraryStore.projectSpatialReference;

      //clever optimization suggested by Ivan
      if (this._lastFoundFace !== undefined) {
        const face = this._lastFoundFace;
        const node1 = this.nodes[face.nodeIndex1];
        const node2 = this.nodes[face.nodeIndex2];
        const node3 = this.nodes[face.nodeIndex3];

        const xmin = Math.min(node1.x, Math.min(node2.x, node3.x));
        const xmax = Math.max(node1.x, Math.max(node2.x, node3.x));
        const ymin = Math.min(node1.y, Math.min(node2.y, node3.y));
        const ymax = Math.max(node1.y, Math.max(node2.y, node3.y));

        if (x >= xmin && x <= xmax && y >= ymin && y <= ymax) {
          //possible hit - point is within bounds, now check to see if point is inside triangle

          // DONE: to improve performance, prebuild/cache points and polygons instead of during each loop
          // const polygon = this.convertFaceToPolygon(face, this.nodes, spatialReference);

          const faceIdx = this.faces.indexOf(face);
          const polygon = this.polygons[faceIdx];
          const point = new Point({ x, y, spatialReference });

          const hitResult = polygon.contains(point);
          if (hitResult) {
            this._lastFoundFace = face;
            return face; //face found, can short circuit
          }
        }
      }

      let compareList: SurfaceTinFace[];
      if (shortList !== undefined) {
        compareList = shortList;
      } else {
        compareList = this.faces;
      }

      for (const face of compareList) {
        const node1 = this.nodes[face.nodeIndex1];
        const node2 = this.nodes[face.nodeIndex2];
        const node3 = this.nodes[face.nodeIndex3];

        const xmin = Math.min(node1.x, Math.min(node2.x, node3.x));
        const xmax = Math.max(node1.x, Math.max(node2.x, node3.x));
        const ymin = Math.min(node1.y, Math.min(node2.y, node3.y));
        const ymax = Math.max(node1.y, Math.max(node2.y, node3.y));

        if (x >= xmin && x <= xmax && y >= ymin && y <= ymax) {
          //possible hit - point is within bounds, now check to see if point is inside triangle

          // DONE: to improve performance, prebuild/cache points and polygons instead of during each loop
          // const polygon = this.convertFaceToPolygon(face, this.nodes, spatialReference);

          const faceIdx = this.faces.indexOf(face);
          const polygon = this.polygons[faceIdx];
          const point = new Point({ x, y, spatialReference });

          const hitResult = polygon.contains(point);
          if (hitResult) {
            this._lastFoundFace = face;
            return face; //face found, can short circuit
          }
        }
      }
    }

    return undefined;
  }

  public findFaces(xmin: number, xmax: number, ymin: number, ymax: number): SurfaceTinFace[] {
    // if (this.polygons.length !== this.faces.length) {
    //   this.rebuild();
    // }
    // const spatialReference = new SpatialReference({ wkid: this.wkid });
    // const extent = new Extent({ spatialReference, xmin, xmax, ymin, ymax });
    // const result: SurfaceTinFace[] = [];
    // let currentIndex = 0;
    // for (const polygon of this.polygons) {
    //   if (extent.intersects(polygon)) {
    //     const face = this.faces[currentIndex];
    //     result.push(face);
    //   }
    //   currentIndex++;
    // }
    // return result;

    const result: SurfaceTinFace[] = [];

    for (const face of this.faces) {
      const node1 = this.nodes[face.nodeIndex1];
      const node2 = this.nodes[face.nodeIndex2];
      const node3 = this.nodes[face.nodeIndex3];

      //get face extent
      const xminB = Math.min(node1.x, node2.x, node3.x);
      const yminB = Math.min(node1.y, node2.y, node3.y);
      const xmaxB = Math.max(node1.x, node2.x, node3.x);
      const ymaxB = Math.max(node1.y, node2.y, node3.y);

      if (this.doExtentsIntersect(xminB, yminB, xmaxB, ymaxB, xmin, ymin, xmax, ymax)) {
        // NOTE: this does not guarantee that face intersects input extent,
        // only that the face's extent intersects the input extent
        result.push(face);
      }
    }

    return result;
  }
  private doExtentsIntersect(
    xminA: number,
    yminA: number,
    xmaxA: number,
    ymaxA: number,
    xminB: number,
    yminB: number,
    xmaxB: number,
    ymaxB: number
  ): boolean {
    // // THEORY:
    // // IF any of 4 corners of extentA is within extentB
    // // OR if any of 4 corners of extentB is within extentA
    // // THEN the two extents must be intersecting, otherwise they are disjoint
    // // NOTE - this method is fallible and may miss cases where extents overlap but corners do not hit opposing extent

    // if (this.isPointWithinExtent(xminA, yminA, xminB, yminB, xmaxB, ymaxB)) return true;
    // if (this.isPointWithinExtent(xminA, ymaxA, xminB, yminB, xmaxB, ymaxB)) return true;
    // if (this.isPointWithinExtent(xmaxA, yminA, xminB, yminB, xmaxB, ymaxB)) return true;
    // if (this.isPointWithinExtent(xmaxA, ymaxA, xminB, yminB, xmaxB, ymaxB)) return true;

    // if (this.isPointWithinExtent(xminB, yminB, xminA, yminA, xmaxA, ymaxA)) return true;
    // if (this.isPointWithinExtent(xminB, ymaxB, xminA, yminA, xmaxA, ymaxA)) return true;
    // if (this.isPointWithinExtent(xmaxB, yminB, xminA, yminA, xmaxA, ymaxA)) return true;
    // if (this.isPointWithinExtent(xmaxB, ymaxB, xminA, yminA, xmaxA, ymaxA)) return true;

    // return false;

    ////////////////////////////////////

    // THEORY:
    // take greater of two xmins and lesser of two xmaxs to determine common x-domain
    // take greater of two ymins and lesser of two ymaxs to determine common y-domain
    // if span of x-domain is less than zero, then input extents are disjoint
    // if span of y-domain is less than zero, then input extents are disjoint
    // if both spans are greater than or equal to zero, then input extents are intersecting

    const xminC = Math.max(xminA, xminB);
    const xmaxC = Math.min(xmaxA, xmaxB);
    if (xmaxC - xminC < 0) return false;

    const yminC = Math.max(yminA, yminB);
    const ymaxC = Math.min(ymaxA, ymaxB);
    if (ymaxC - yminC < 0) return false;

    return true;
  }

  private isPointWithinExtent(x: number, y: number, xmin: number, ymin: number, xmax: number, ymax: number): boolean {
    // THEORY:
    // if x AND y are BOTH within the respective min/max ranges, then the point is within the extent
    return x >= xmin && x <= xmax && y >= ymin && y <= ymax;
  }

  public interpolateZ(p0: SurfaceTinNode, p1: SurfaceTinNode, p2: SurfaceTinNode, x: number, y: number): number {
    const EPSILON = 1.0e-4;
    // apply the interpolation formula, from Volume.interpolate,
    // adapted to use with XyzPoints
    //
    const aa = (p1.y - p0.y) * (p2.z - p0.z) - (p1.z - p0.z) * (p2.y - p0.y);
    const bb = (p1.z - p0.z) * (p2.x - p0.x) - (p1.x - p0.x) * (p2.z - p0.z);
    const cc = (p1.x - p0.x) * (p2.y - p0.y) - (p1.y - p0.y) * (p2.x - p0.x);

    if (Math.abs(cc) > EPSILON) {
      const elevation = p0.z - (aa * (x - p0.x) + bb * (y - p0.y)) / cc;
      return elevation;
    } else {
      // degenerate case, cc = 0, interp. would be divide by zero

      // if requested point is in one corner, return that corner Z
      const pt = new SurfaceTinNode(x, y, 0);
      const d0 = this.getDistance2D(pt, p0);
      if (d0 < EPSILON) return p0.z;
      const d1 = this.getDistance2D(pt, p1);
      if (d1 < EPSILON) return p1.z;
      const d2 = this.getDistance2D(pt, p2);
      if (d2 < EPSILON) return p2.z;

      // failing that, use weighted average
      // Ludicrously expensive (3 sqrt + 4 div) but should be rare...
      const prox0 = 1.0 / d0;
      const prox1 = 1.0 / d1;
      const prox2 = 1.0 / d2;
      const zAve = (prox0 * p0.z + prox1 * p1.z + prox2 * p2.z) / (prox0 + prox1 + prox2);
      return zAve;
    }
  }

  private getDistance2D(ptA: SurfaceTinNode, ptB: SurfaceTinNode) {
    const deltaX = ptB.x - ptA.x;
    const deltaY = ptB.y - ptA.y;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  }

  computeExtent(): Extent {
    const spatialReference = new SpatialReference({ wkid: 3857 }); // Web Mercator
    let xmin: number | undefined;
    let xmax: number | undefined;
    let ymin: number | undefined;
    let ymax: number | undefined;
    let zmin: number | undefined;
    let zmax: number | undefined;

    for (const node of this.nodes) {
      if (xmin === undefined) {
        xmin = node.x;
        xmax = node.x;
        ymin = node.y;
        ymax = node.y;
        zmin = node.z;
        zmax = node.z;
      } else {
        xmin = Math.min(xmin!, node.x);
        xmax = Math.max(xmax!, node.x);
        ymin = Math.min(ymin!, node.y);
        ymax = Math.max(ymax!, node.y);
        zmin = Math.min(zmin!, node.z);
        zmax = Math.max(zmax!, node.z);
      }
    }
    // const hasZ = true;

    this.extent = new Extent({ xmin, xmax, ymin, ymax, zmin, zmax, spatialReference });
    return this.extent;
  }

  // convertNodeToPoint(node: SurfaceTinNode, spatialReference: SpatialReference): Point {
  //   const point = new Point();
  //   point.spatialReference = spatialReference;
  //   point.hasZ = true;
  //   point.x = node.x;
  //   point.y = node.y;
  //   point.z = node.z;
  //   return point;
  // }

  // convertFaceToPolygon(face: SurfaceTinFace, nodes: SurfaceTinNode[], spatialReference: SpatialReference): Polygon {
  //   const node1 = nodes[face.nodeIndex1];
  //   const node2 = nodes[face.nodeIndex2];
  //   const node3 = nodes[face.nodeIndex3];
  //   const point1 = this.convertNodeToPoint(node1, spatialReference);
  //   const point2 = this.convertNodeToPoint(node2, spatialReference);
  //   const point3 = this.convertNodeToPoint(node3, spatialReference);
  //   const polygon = new Polygon();
  //   polygon.spatialReference = spatialReference;
  //   polygon.hasZ = true;
  //   polygon.addRing([point1, point2, point3]);
  //   return polygon;
  // }

  private rebuildPoints() {
    this.points = [];
    const spatialReference = projectsLibraryStore.projectSpatialReference!;
    for (const node of this.nodes) {
      const point = new Point();
      point.spatialReference = spatialReference;
      point.hasZ = true;
      point.x = node.x;
      point.y = node.y;
      point.z = node.z;
      this.points.push(point);
    }
  }

  private rebuildPolygons() {
    this.polygons = [];
    const spatialReference = projectsLibraryStore.projectSpatialReference!;
    for (const face of this.faces) {
      const polygon = new Polygon();
      polygon.spatialReference = spatialReference;
      polygon.hasZ = true;

      const point1 = this.points[face.nodeIndex1];
      const point2 = this.points[face.nodeIndex2];
      const point3 = this.points[face.nodeIndex3];
      const ring = [point1, point2, point3];
      polygon.addRing(ring);

      this.polygons.push(polygon);
    }
  }

  public rebuild() {
    // console.log('REBUILD!!!!!!');
    this.rebuildPoints();
    this.rebuildPolygons();
  }
}
